import { useQuery } from '@apollo/client';
import React, { useState, useEffect, useMemo } from 'react';
import { gqlTypes } from '../../../types/gqlTypes';
import { allBreedsQuery } from './BreedSelect.graphql';
import { ErrorMessage, useFormikContext } from 'formik';
import classNames from 'classnames';
import RadioField from '../../../components/form/RadioField';
import BreedSelectModal from './BreedSelectModal';
import Checkbox from '../../../components/Checkbox';

export const UNKNOWN_BREED_NAME = 'Other';

export interface BreedFields {
  breedId: string;
  isMixedBreed: boolean;
  species: string;
}

interface BreedSelectProps {
  name: string;
  isInline?: boolean;
  isMixedBreed: boolean;
}

const SPECIES_OPTIONS = [
  {
    label: `Dog`,
    value: gqlTypes.Species.DOG,
  },
  {
    label: `Cat`,
    value: gqlTypes.Species.CAT,
  },
  {
    label: `Other`,
    value: gqlTypes.Species.OTHER,
  },
];

const PUREBRED_OPTIONS = [
  {
    label: `Purebred`,
    value: 'purebred',
  },
  {
    label: `Mixed`,
    value: 'mixed',
  },
];

export default function BreedSelect({ name, isInline }: BreedSelectProps) {
  const { setFieldValue, values } = useFormikContext<BreedFields>();

  const [value, setValue] = useState<string>(() => values.breedId || '');
  const species = useMemo(() => values.species || gqlTypes.Species.DOG, [values.species]);

  const { data } = useQuery<gqlTypes.NANO_RegisterChipV2_allBreeds>(allBreedsQuery, {
    variables: { species },
  });
  const options = useMemo(() => {
    const breeds = data?.allBreeds ?? [];

    return breeds.map((breed) => ({
      label: breed.name,
      value: breed.id,
    }));
  }, [data?.allBreeds]);

  const unknownBreed = data?.allBreeds.find((breed) => breed.name === UNKNOWN_BREED_NAME);

  // Track "I don't know" state - true when the selected breed is "Other"
  const isUnknownBreedSelected = unknownBreed && value === unknownBreed.id;

  useEffect(() => {
    // If we've got a mismatched species, then just reset to the "Other" breed for that species
    const selectedBreed = data?.allBreeds.find((breed) => breed.id === value);
    if (value && data && selectedBreed?.species !== species) {
      setValue(unknownBreed?.id ?? '');
    }
  }, [values.species, species, unknownBreed, data, value, values]);

  useEffect(() => {
    if (values.breedId !== value) {
      setFieldValue(name, value);
    }
  }, [value, name, setFieldValue, values.breedId]);

  const handleUnknownBreedChange = (checked: boolean) => {
    if (checked && unknownBreed) {
      setValue(unknownBreed.id);
    } else {
      setValue('');
    }
  };

  const handlePurebredChange = (purebredValue: string) => {
    setFieldValue('isMixedBreed', purebredValue === 'mixed');
  };

  const purebredValue = values.isMixedBreed ? 'mixed' : 'purebred';

  return (
    <>
      <div className="form-group">
        <div className="form-field">
          <RadioField
            options={SPECIES_OPTIONS}
            name="species"
            label="Species"
            selectedValue={values.species || gqlTypes.Species.DOG}
          />
          {species !== gqlTypes.Species.DOG && <div className="form-field__hint">Breed selection only for dogs</div>}
        </div>
      </div>
      {species === gqlTypes.Species.DOG && (
        <>
          <div className={classNames('form-group')}>
            <div className="form-field">
              <RadioField
                options={PUREBRED_OPTIONS}
                name="purebredSelector"
                label="Breed"
                selectedValue={purebredValue}
                onChange={handlePurebredChange}
              />
            </div>
          </div>
          <div className={classNames('form-group')}>
            <div className="form-field">
              <BreedSelectModal
                options={options}
                setValue={setValue}
                value={values.breedId}
                variant={isInline ? 'transparent' : undefined}
              />
            </div>
            {unknownBreed && (
              <div className="form-field">
                <Checkbox
                  checked={!!isUnknownBreedSelected}
                  onChange={handleUnknownBreedChange}
                  label="I don't know the breed"
                />
              </div>
            )}
          </div>
        </>
      )}

      <ErrorMessage name={name} component="div" className="form-field__error" />
    </>
  );
}
